import { IRefIds } from 'constants/refs';
import { get } from 'services/request';
import { BaseModel, IModelOptions } from './BaseModel';
import { IAgentQAItem, IAgentQAGroupedItem, AgentQAItemStatus } from 'types/agentQA';
import { AsyncTaskStatus } from 'constants/conceptLinkerValidatorStatus';
import { AgentQAModel } from './AgentQAModel';

export default class AgentQAItemsModel extends BaseModel<IAgentQAGroupedItem, null> {
  jobId?: number;
  private agentQAModel?: AgentQAModel;

  constructor(options: IModelOptions & { jobId?: number }) {
    super(options);
    this.jobId = options.jobId;
    this.ref = IRefIds.agentQA;
    this.gridName = 'AgentQAItems';
    this.getGridColumns();
  }

  setJobId(jobId: number) {
    this.jobId = jobId;
    this.gridData = [];
    this.getGrid();
  }

  async getGrid() {
    if (!this.jobId) return;

    await super.getGrid();
    const flatItems = await get<IAgentQAItem[]>(`/agent-qa/${this.jobId}/items`);
    this.gridData = this.groupItemsByEntity(flatItems);
    this.loadingGrid = false;
    this.render();
  }

  private groupItemsByEntity(items: IAgentQAItem[]): IAgentQAGroupedItem[] {
    const groups = new Map<string, IAgentQAGroupedItem>();

    items.forEach((item) => {
      const info = item.item_info;
      // Use entity_id + ref + dialect_code as the grouping key (unique identifier for the source item)
      const key = `${info?.entity_id || ''}_${info?.ref || ''}_${info?.dialect_code || ''}`;

      if (!groups.has(key)) {
        groups.set(key, {
          id: item.id,
          entity_key: key,
          item_info: info,
          navigation_data: item.navigation_data,
          runs: [],
          pass_count: 0,
          fail_count: 0,
          pending_count: 0,
          error_count: 0,
          async_task_status: item.async_task_status,
          agent_prompt_log: item.agent_prompt_log,
        });
      }

      // Update async_task_status to show the "worst" status (FAILED > DOING > PENDING > DONE)
      const group = groups.get(key)!;
      const currentStatus = group.async_task_status;
      const newStatus = item.async_task_status;
      if (
        newStatus === AsyncTaskStatus.FAILED ||
        newStatus === AsyncTaskStatus.DEAD_LETTER ||
        newStatus === AsyncTaskStatus.CANCELLED
      ) {
        group.async_task_status = newStatus;
      } else if (
        newStatus === AsyncTaskStatus.DOING &&
        currentStatus !== AsyncTaskStatus.FAILED &&
        currentStatus !== AsyncTaskStatus.DEAD_LETTER &&
        currentStatus !== AsyncTaskStatus.CANCELLED
      ) {
        group.async_task_status = newStatus;
      } else if (
        newStatus === AsyncTaskStatus.PENDING &&
        currentStatus === AsyncTaskStatus.DONE
      ) {
        group.async_task_status = newStatus;
      }

      group.runs.push({
        id: item.id,
        run_number: item.run_number,
        status: item.status,
        test_result: item.test_result,
        actual_result: item.actual_result,
        expected_result: item.expected_result,
        test_metadata: item.test_metadata,
        async_task_status: item.async_task_status,
        agent_prompt_log: item.agent_prompt_log,
      });

      // Update counts
      if (item.status === AgentQAItemStatus.ERROR) {
        group.error_count++;
      } else if (item.status === AgentQAItemStatus.PENDING || item.status === AgentQAItemStatus.PROCESSING) {
        group.pending_count++;
      } else if (item.test_result === 'pass') {
        group.pass_count++;
      } else if (item.test_result === 'fail') {
        group.fail_count++;
      }
    });

    // Sort runs by run_number within each group
    groups.forEach((group) => {
      group.runs.sort((a, b) => a.run_number - b.run_number);
    });

    return Array.from(groups.values());
  }

  async refresh() {
    await this.getGrid();
  }

  async reload() {
    await super.reload();
    await this.getGrid();
  }

  async loadItemData(itemId: number) {
    return {};
  }

  async getGridRow(itemId: number) {
    // Find the grouped item that contains this run id
    const group = this.gridData?.find((g: IAgentQAGroupedItem) => g.runs.some((r) => r.id === itemId));
    return group || { id: itemId, entity_key: String(itemId) };
  }

  setAgentQAModel(model: AgentQAModel) {
    this.agentQAModel = model;
  }

  async retryItem(entityKey: string): Promise<boolean> {
    if (!this.jobId || !this.agentQAModel) return false;

    try {
      const result = await this.agentQAModel.retryItem(this.jobId, entityKey);

      // Update the row locally with the returned items
      if (result.data?.items && result.data.items.length > 0) {
        this.updateRowFromItems(entityKey, result.data.items);
      }

      return true;
    } catch (error) {
      console.error('Error retrying item:', error);
      return false;
    }
  }

  private updateRowFromItems(entityKey: string, items: IAgentQAItem[]) {
    if (!this.gridData) return;

    // Find the existing row with matching entity_key
    const existingRow = this.gridData.find((g: IAgentQAGroupedItem) => g.entity_key === entityKey);
    if (!existingRow) return;

    // Re-group the items to get the updated grouped item
    const updatedGroups = this.groupItemsByEntity(items);
    if (updatedGroups.length === 0) return;

    // Keep the same id for ag-grid to find the row
    const updatedGroup = updatedGroups[0];
    updatedGroup.id = existingRow.id;

    // Find and replace in gridData
    const rowIndex = this.gridData.findIndex((g: IAgentQAGroupedItem) => g.entity_key === entityKey);
    if (rowIndex !== -1) {
      this.gridData[rowIndex] = updatedGroup;
    }

    // Trigger grid update using the proper mechanism
    this.gridDataUpdateRow = { type: 'update', data: updatedGroup };
    this.render();
  }
}
